import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import Menu from './Menu';
import UserButton from './UserButton';
import { useTheme } from '../../context/ThemeContext';

// ── Variante de animación estática (nivel módulo) ────────────────────────
// Solo controlsContainer es estático (no depende de useReducedMotion).
// headerVariants y controlItem se resuelven dentro del componente
// para incorporar shouldReduceMotion dinámicamente.
const controlsContainer = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.07, delayChildren: 0.2 } }
};
// ──────────────────────────────────────────────────────────────────────────

const Header = ({ onReset }) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  // animation-designer § Reduce Motion — accesibilidad para usuarios con
  // prefers-reduced-motion activo. Si true, no hay y-offset ni duración.
  const shouldReduceMotion = useReducedMotion();

  const resolvedHeaderVariants = {
    hidden: { opacity: 0, y: shouldReduceMotion ? 0 : -8 },
    show: {
      opacity: 1, y: 0,
      transition: { duration: shouldReduceMotion ? 0 : 0.35, ease: 'easeOut' }
    }
  };

  const resolvedControlItem = {
    hidden: { opacity: 0, y: shouldReduceMotion ? 0 : -6 },
    show: {
      opacity: 1, y: 0,
      transition: { duration: shouldReduceMotion ? 0 : 0.25, ease: 'easeOut' }
    }
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <>
      {/* ─── HEADER UNIFICADO ───────────────────────────────────────────────
          IMPORTANTE: La animación está en un <motion.div> INTERNO.
          El <header> nativo NO lleva transform: evita crear un nuevo
          stacking context que hundiría el dropdown del UserButton.
      ────────────────────────────────────────────────── */}
      <header
        id="main-header"
        className="
          fixed top-0 left-0 w-full z-40
          h-16 md:h-20
          border-b border-[#FAB0C0]/25
        "
      >
        {/* Wrapper animado — el transform vive aquí, NO en el header */}
        <motion.div
          variants={resolvedHeaderVariants}
          initial="hidden"
          animate="show"
          className="
            w-full h-full
            flex items-center justify-between
            px-4 sm:px-6
            relative
          "
        >
          {/* Capa de fondo — glass effect (interface-design § Surface Elevation)
            backdrop-blur-xl > md: el header flota visualmente sobre el contenido.
            bg-black/75 más transparente para que el blur sea el protagonista. */}
          <div className="absolute inset-0 bg-black/75 backdrop-blur-xl -z-10" />

          {/* Toque atmosférico: gradiente diagonal rosa→turquesa a 5% opacidad.
            frontend-design § Backgrounds & Visual Details: colores del SVG
            del logo filtrados hacia el fondo del header. Imperceptible pero presente. */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#FAB0C0]/5 via-transparent to-[#48E5B9]/5 -z-10 pointer-events-none" />

          {/* ── Zona Izquierda: Botón Hamburguesa ──────────────────────────
            Sin whileHover/whileTap: es un botón de estado (abre menú),
            no de acción puntual. El color hover es suficiente feedback. */}
          <button
            onClick={toggleMenu}
            className="
            p-2 rounded-md text-white flex-shrink-0
            hover:bg-white/20 transition-colors
            focus:outline-none focus:ring-2 focus:ring-[#FAB0C0]/50 focus:ring-offset-0
          "
            aria-label={t('Header.tooltip_menu', 'Abrir menú')}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" />
            </svg>
          </button>

          {/* ── Zona Centro: Logo — centrado absolutamente ─────────────────
            Se usa absolute + left-1/2 + -translate-x-1/2 para garantizar
            que el logo siempre esté en el centro REAL del header,
            independientemente del ancho de las zonas izquierda y derecha. */}
          <div className="absolute left-1/2 -translate-x-1/2 flex items-center pointer-events-none">
            <Link to="/" className="flex items-center pointer-events-auto">
              {/* Logo solo ícono — móvil */}
              <img
                src="/images/Logo/Umacalculator_logo.svg"
                alt="Umacalculator"
                className="h-10 w-auto md:hidden object-contain"
              />
              {/* Logo con texto — desktop. viewBox 4403×807 → a h-10: ~218px de ancho */}
              <img
                src="/images/Logo/Umacalculator_logo_text.svg"
                alt="Umacalculator"
                className="hidden md:block h-10 w-auto object-contain"
              />
            </Link>
          </div>

          {/* ── Zona Derecha: Controles ────────────────────────────────────
            Entrada escalonada: controlsContainer + controlItem.
            animation-designer § Pattern 2: delight in staggered reveals. */}
          <motion.div
            className="flex items-center gap-3"
            variants={controlsContainer}
            initial="hidden"
            animate="show"
          >
            {/* Toggle de Idioma
              interface-design § Typography: tracking-widest en texto
              corto (ES/EN) lo transforma de label genérico a badge premium.
              whileHover + whileTap: animation-designer § Pattern 3.
              ease (no spring): interface-design prohíbe spring en apps profesionales. */}
            <motion.button
              onClick={() => i18n.changeLanguage(i18n.language.startsWith('es') ? 'en' : 'es')}
              variants={resolvedControlItem}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.93 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="
              p-2 w-10 h-10 flex items-center justify-center rounded-lg
              hover:bg-white/20 text-white transition-colors
              font-bold text-xs tracking-widest uppercase
              border border-white/20
              focus:outline-none focus:ring-2 focus:ring-[#FAB0C0]/50 focus:ring-offset-0
            "
              title={t('Header.tooltip_language')}
            >
              {i18n.language.startsWith('es') ? 'ES' : 'EN'}
            </motion.button>

            {/* Toggle de Tema (Dark / Light) */}
            <motion.button
              onClick={toggleTheme}
              variants={resolvedControlItem}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.93 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="
              p-2 rounded-lg text-white transition-colors hover:bg-white/20
              focus:outline-none focus:ring-2 focus:ring-[#FAB0C0]/50 focus:ring-offset-0
            "
              title={theme === 'dark' ? t('Header.tooltip_light') : t('Header.tooltip_dark')}
            >
              {theme === 'dark' ? (
                <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </motion.button>

            {/* Botón Reset — condicional.
              Sin whileHover/whileTap: es destructivo, el color rojo en hover
              es el feedback correcto. Scale en botones peligrosos confunde. */}
            {onReset && (
              <motion.button
                onClick={onReset}
                variants={resolvedControlItem}
                className="
                p-2 rounded-lg transition-colors
                hover:bg-red-500/20 text-red-400 hover:text-red-300
                focus:outline-none focus:ring-2 focus:ring-red-400/50 focus:ring-offset-0
              "
                title={t('Header.tooltip_reset')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </motion.button>
            )}

            {/* Botón de Usuario — self-contained, sin cambios */}
            <motion.div variants={resolvedControlItem}>
              <UserButton />
            </motion.div>
          </motion.div>
        </motion.div>   {/* ← cierre del wrapper animado */}
      </header>

      {/* Spacer: compensa el header fixed (h-16 móvil / h-20 desktop).
          El header anterior (#main-header relative h-40) dejaba 160px de espacio.
          Este div reemplaza ese espacio con la altura correcta del nuevo nav. */}
      <div className="h-16 md:h-20" aria-hidden="true" />

      {/* Menú lateral — props idénticas al original */}
      <Menu isOpen={isMenuOpen} closeMenu={toggleMenu} />
    </>
  );
};

export default Header;
// Revisado: 2025-12-18 [I18n Scan]
